import React, { useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import secureLocalStorage from "react-secure-storage";
import toast, { Toaster } from "react-hot-toast";
import { api } from "../../utils/axios-instance";
import { auth } from "../../helper/firebaseClient";
import { useDarkMode } from "../../context/darkModeContext";

const SUPER_ADMIN_LABEL = "Super Admins";
const OTHER_GROUP_KEY = "__other__";
const DEFAULT_FADED_BG = "#F3F4F6"; // gray-100 — used when a dept has no color set
const DEFAULT_BORDER = "#D8DEE4";

// Mixes a hex color toward white so a department's brand color becomes a
// soft/pastel background instead of the full-saturation color — a solid
// "#7C3AED" behind a data table is loud and fights with text; a faded tint
// reads as "grouped under this department" without hurting readability.
const fadeHex = (hex, amount = 0.85) => {
  if (!hex || typeof hex !== "string" || hex[0] !== "#") return null;

  let h = hex.slice(1);
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  if (h.length !== 6) return null;

  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const mix = (c) => Math.round(c + (255 - c) * amount);

  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
};

// One department (or the Super Admins) section: header + its DataGrid.
// Pulled out as its own component so the super-admin group and every
// department group render identically and stay in sync.
const LobbyGroupTable = ({
  label,
  rows,
  faded,
  border,
  darkMode,
  revokingId,
  onAccept,
  onRevoke,
  selectedIds,
  onSelectionChange,
}) => {
  const bg = darkMode ? "#1E293B" : faded;
  const textColor = darkMode ? "#E2E8F0" : "#111827"; // near-black in light mode for clear contrast on the faded tint

  const rowIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const selectionModel = useMemo(
    () => rowIds.filter((id) => selectedIds.has(id)),
    [rowIds, selectedIds]
  );

  return (
    <div style={{ width: "100%" }} className="my-5">
      <h3
        className="font-semibold text-[18px] mb-3"
        style={{ color: darkMode ? "#fff" : "#111827" }}
      >
        <span className="flex flex-row gap-1 items-center">
          <p>{label}</p>
          <div className="rounded-full w-2 h-2 bg-zinc-500 mx-1" />
          <p className="text-zinc-700">{rows.length}</p>
        </span>
      </h3>

      <DataGrid
        rowHeight={58}
        columnHeaderHeight={52}
        getRowId={(row) => row?.id || row?.email}
        checkboxSelection
        hideFooterSelectedRowCount
        disableRowSelectionOnClick
        disableColumnMenu
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(newModel) =>
          onSelectionChange(rowIds, newModel)
        }
        sx={{
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: bg,
            minHeight: "52px !important",
            maxHeight: "52px !important",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
            color: textColor,
          },
          "& .MuiDataGrid-cell": {
            fontSize: "14px",
            color: textColor,
          },
          "& .MuiDataGrid-row": {
            minHeight: "58px !important",
            maxHeight: "58px !important",
          },
          "& .MuiDataGrid-columnHeaderCheckbox": {
            visibility: "hidden",
          },
        }}
        style={{
          backgroundColor: bg,
          border: `2px solid ${border}`,
        }}
        rows={rows}
        columns={[
          {
            field: "employee_id",
            headerName: <p className="font-bold">Employee Id</p>,
            flex: 0.5,
            minWidth: 0,
            renderCell: (params) => (
              <div className="flex items-center" style={{ color: textColor }}>
                {params.row.employeeId || "#767asv"}
              </div>
            ),
          },
          {
            field: "name",
            headerName: <p className="font-bold">Name</p>,
            flex: 1,
            minWidth: 160,
            renderCell: (params) => (
              <div className="flex items-center" style={{ color: textColor }}>
                <Tooltip title={params.row.email} arrow>
                  <Avatar
                    src={params.row.profilePictureUrl}
                    alt={`${params.row.name} ${params.row.last_name || ""}`}
                    sx={{
                      marginRight: 1,
                      width: "30px",
                      height: "30px",
                      borderRadius: "25%",
                    }}
                    variant="rounded"
                  />
                </Tooltip>
                {`${params.row.name || ""} ${params.row.last_name || ""}`}
              </div>
            ),
          },
          {
            field: "email",
            headerAlign: "center",
            align: "center",
            headerName: <p className="font-bold">Email</p>,
            width: 240,
            hideSortIcons: true,
          },
          {
            field: "role_priv",
            headerName: <p className="font-bold">Designation</p>,
            flex: 1,
            minWidth: 100,
            renderCell: (params) => {
              const raw = params.row?.role_priv || "employee";
              const role = raw.charAt(0).toUpperCase() + raw.slice(1);
              return <div style={{ color: textColor }}>{role}</div>;
            },
          },
          {
            field: "action",
            headerName: <p className="font-bold">Action</p>,
            flex: 2,
            minWidth: 200,
            headerAlign: "center",
            hideSortIcons: true,
            align: "center",
            renderCell: (params) => {
              const isOnboarded = params?.row?.status === "onboarded";

              if (!isOnboarded) {
                return (
                  <div className="text-yellow-800">
                    Onboarding is in progress
                  </div>
                );
              }

              const isLoading = revokingId === params.row.id;

              return (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onAccept(params.row)}
                    className="py-2 px-4 rounded-lg border bg-white shadow-sm border-zinc-400"
                  >
                    Accept
                  </button>
                  <button
                    disabled={isLoading}
                    onClick={() => onRevoke(params.row.id)}
                    className="py-2 px-4 rounded-lg shadow-sm bg-red-500 font-medium text-white disabled:opacity-50"
                  >
                    {isLoading ? "Revoking..." : "Revoke"}
                  </button>
                </div>
              );
            },
          },
        ]}
        pageSize={5}
      />
    </div>
  );
};

const Lobby = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const { darkMode } = useDarkMode();

  const departments = JSON.parse(
    secureLocalStorage.getItem("departments") || "[]"
  );

  useEffect(() => {
    const listUsers = async () => {
      try {
        const response = await api.get("/users/list_lobby_users");

        const data = response?.data || [];
        setUsers(data);

        const waitingUsers = data.filter((user) => {
          const approved =
            user?.flags?.is_approved ?? user?.is_approved ?? false;
          return !approved;
        });
        setFilteredUsers(waitingUsers);
      } catch (error) {
        console.log(error);
        toast.error("Failed to load lobby users");
      } finally {
        setLoading(false);
      }
    };

    listUsers();
  }, []);

  // Super Admins are pulled into their own top group with a plain
  // background (they're org-wide, not department-scoped). Everyone else
  // is grouped by department — and since an employee can belong to more
  // than one department, they intentionally appear once per department
  // they're in, each time tinted with that department's own color.
  const { superAdmins, deptGroups } = useMemo(() => {
    const admins = [];
    const byDept = {};

    filteredUsers.forEach((user) => {
      const isSuperAdmin =
        (user.role_priv || "").toLowerCase() === "super_admin";

      if (isSuperAdmin) {
        admins.push(user);
        return;
      }

      const deptIds = user.dept_ids?.length
        ? user.dept_ids
        : user.dept
          ? [user.dept]
          : [];

      if (deptIds.length === 0) {
        if (!byDept[OTHER_GROUP_KEY]) byDept[OTHER_GROUP_KEY] = [];
        byDept[OTHER_GROUP_KEY].push(user);
        return;
      }

      deptIds.forEach((deptId) => {
        if (!byDept[deptId]) byDept[deptId] = [];
        byDept[deptId].push(user);
      });
    });

    // Known departments first, in the org's own department order.
    const groups = departments
      .filter((d) => byDept[d.id]?.length)
      .map((d) => ({
        key: d.id,
        label: d.name,
        rows: byDept[d.id],
        faded: fadeHex(d?.metadata?.bg) || DEFAULT_FADED_BG,
        border: d?.metadata?.border || DEFAULT_BORDER,
      }));

    // Any dept id referenced by a user but no longer in the department
    // list (deleted department, etc.) — still surface it instead of
    // silently dropping that person from the lobby.
    Object.keys(byDept).forEach((key) => {
      if (key === OTHER_GROUP_KEY) return;
      if (departments.some((d) => d.id === key)) return;
      groups.push({
        key,
        label: byDept[key][0]?.dept_name || byDept[key][0]?.dept || "Other",
        rows: byDept[key],
        faded: DEFAULT_FADED_BG,
        border: DEFAULT_BORDER,
      });
    });

    if (byDept[OTHER_GROUP_KEY]?.length) {
      groups.push({
        key: OTHER_GROUP_KEY,
        label: "Other",
        rows: byDept[OTHER_GROUP_KEY],
        faded: DEFAULT_FADED_BG,
        border: DEFAULT_BORDER,
      });
    }

    return { superAdmins: admins, deptGroups: groups };
  }, [filteredUsers, departments]);

  // Every unique row id currently showing anywhere on the page — a user
  // can appear in several department tables, but only counts once here.
  const allRowIds = useMemo(() => {
    const ids = new Set(superAdmins.map((u) => u.id));
    deptGroups.forEach((g) => g.rows.forEach((u) => ids.add(u.id)));
    return ids;
  }, [superAdmins, deptGroups]);

  const allSelected =
    allRowIds.size > 0 && [...allRowIds].every((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(allRowIds));
  };

  // A given table's checkbox selection changed — merge it back into the
  // global selection without disturbing selections made in other tables
  // (important since the same user id can be selected from more than one
  // department table).
  const handleGroupSelectionChange = (groupRowIds, newModel) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      groupRowIds.forEach((id) => next.delete(id));
      newModel.forEach((id) => next.add(id));
      return next;
    });
  };

  const dropIdEverywhere = (id) => {
    setFilteredUsers((prev) => prev.filter((u) => u.id !== id));
    setUsers((prev) => prev.filter((u) => u.id !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleRevokeUserClick = async (id) => {
    if (!id) {
      toast.error("Missing user id");
      return;
    }

    try {
      setRevokingId(id);
      await api.post(`/users/revoke-lobby-user`, { userId: id });
      toast.success("User revoked successfully.");
      dropIdEverywhere(id);
    } catch (error) {
      console.error(error);
      toast.error("error occured  while revoking user");
    } finally {
      setRevokingId(null);
    }
  };

  const acceptUser = async (row) => {
    try {
      const id = row?.id;

      if (!id) {
        toast.error("Missing user id");
        return;
      }

      await auth.currentUser.getIdToken();

      if (row?.status !== "onboarded") {
        toast.error("User hasn't completed onboarding yet.");
        return;
      }

      await api.patch(`/users/elevate/${id}`, {
        "flags.is_approved": true,
      });

      toast.success("User approved successfully.");
      dropIdEverywhere(id);
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        "No seats available or onboarding incomplete.";
      toast.error(msg);
    }
  };

  const findRowById = (id) => filteredUsers.find((u) => u.id === id);

  const handleBulkAccept = async () => {
    const ids = [...selectedIds];
    const eligible = ids.filter(
      (id) => findRowById(id)?.status === "onboarded"
    );
    const ineligible = ids.length - eligible.length;

    if (eligible.length === 0) {
      toast.error("None of the selected users have completed onboarding yet.");
      return;
    }

    setBulkLoading(true);

    const results = await Promise.allSettled(
      eligible.map((id) =>
        api.patch(`/users/elevate/${id}`, { "flags.is_approved": true })
      )
    );

    const succeeded = eligible.filter(
      (_, i) => results[i].status === "fulfilled"
    );
    const failed = eligible.length - succeeded.length;

    if (succeeded.length > 0) {
      setFilteredUsers((prev) => prev.filter((u) => !succeeded.includes(u.id)));
      setUsers((prev) => prev.filter((u) => !succeeded.includes(u.id)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        succeeded.forEach((id) => next.delete(id));
        return next;
      });
    }

    setBulkLoading(false);

    if (succeeded.length > 0) {
      toast.success(
        `Approved ${succeeded.length} user${succeeded.length === 1 ? "" : "s"}.`
      );
    }
    if (failed > 0 || ineligible > 0) {
      const count = failed + ineligible;
      toast.error(
        `${count} user${count === 1 ? "" : "s"} couldn't be approved (no seats available or onboarding incomplete).`
      );
    }
  };

  const handleBulkRevoke = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;

    setBulkLoading(true);

    const results = await Promise.allSettled(
      ids.map((id) => api.post(`/users/revoke-lobby-user`, { userId: id }))
    );

    const succeeded = ids.filter((_, i) => results[i].status === "fulfilled");
    const failed = ids.length - succeeded.length;

    if (succeeded.length > 0) {
      setFilteredUsers((prev) => prev.filter((u) => !succeeded.includes(u.id)));
      setUsers((prev) => prev.filter((u) => !succeeded.includes(u.id)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        succeeded.forEach((id) => next.delete(id));
        return next;
      });
    }

    setBulkLoading(false);

    if (succeeded.length > 0) {
      toast.success(
        `Revoked ${succeeded.length} user${succeeded.length === 1 ? "" : "s"}.`
      );
    }
    if (failed > 0) {
      toast.error(`${failed} revoke${failed === 1 ? "" : "s"} failed.`);
    }
  };

  if (loading) {
    return <div className="text-center my-4">Loading...</div>;
  }

  if (filteredUsers.length < 1) {
    return <div className="text-center my-4">Nobody's in the lobby.</div>;
  }

  return (
    <div className="p-5">
      <Toaster position="bottom-left" reverseOrder={false} />

      {/* Global selection bar — selects/deselects every row across every
          table below, and drives the bulk accept/revoke actions. */}
      <div className="flex items-center justify-between mb-2 px-1">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <Checkbox
            checked={allSelected}
            indeterminate={!allSelected && selectedIds.size > 0}
            onChange={toggleSelectAll}
            sx={{ padding: 0 }}
          />
          <span
            className="font-medium"
            style={{ color: darkMode ? "#fff" : "#111827" }}
          >
            Select all
          </span>
          {selectedIds.size > 0 && (
            <span className="text-sm text-zinc-500">
              ({selectedIds.size} selected)
            </span>
          )}
        </label>

        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkAccept}
              disabled={bulkLoading}
              className="py-2 px-4 rounded-lg border bg-white shadow-sm border-zinc-400 disabled:opacity-50"
            >
              {bulkLoading
                ? "Processing..."
                : `Accept Selected (${selectedIds.size})`}
            </button>
            <button
              onClick={handleBulkRevoke}
              disabled={bulkLoading}
              className="py-2 px-4 rounded-lg shadow-sm bg-red-500 font-medium text-white disabled:opacity-50"
            >
              {bulkLoading
                ? "Processing..."
                : `Revoke Selected (${selectedIds.size})`}
            </button>
          </div>
        )}
      </div>

      {superAdmins.length > 0 && (
        <LobbyGroupTable
          label={SUPER_ADMIN_LABEL}
          rows={superAdmins}
          faded="#FFFFFF"
          border={DEFAULT_BORDER}
          darkMode={darkMode}
          revokingId={revokingId}
          onAccept={acceptUser}
          onRevoke={handleRevokeUserClick}
          selectedIds={selectedIds}
          onSelectionChange={handleGroupSelectionChange}
        />
      )}

      {deptGroups.map((group) => (
        <LobbyGroupTable
          key={group.key}
          label={group.label}
          rows={group.rows}
          faded={group.faded}
          border={group.border}
          darkMode={darkMode}
          revokingId={revokingId}
          onAccept={acceptUser}
          onRevoke={handleRevokeUserClick}
          selectedIds={selectedIds}
          onSelectionChange={handleGroupSelectionChange}
        />
      ))}
    </div>
  );
};

export default Lobby;
